import React from "react";
import { GoalType } from "./goal_type_class";
import { GoalTemplate } from "./goal_template_class";
import {
  GoalTypeShape,
  InterviewGoalShape,
  InterviewSetShape,
  NewInterviewShape,
  SavePayloadShape,
} from "../interfaces/classTypes";
import { NewInterview } from "./new_interview_class";
import { InterviewSet } from "./interview_set_class";

export class InterviewGoalTemplate extends GoalTemplate {
  public achievementTarget: number;
  public achievementThreshold: number;
  public interviewSetIds: number[] | null;
  public selectedInterviewSets: InterviewSetShape[];
  public type: GoalTypeShape;

  constructor({
    achievementTargetFormatted = null,
    achievementTarget = null,
    achievementThreshold = null,
    continuous = true,
    createdAtFormatted = null,
    description = "",
    hasErrors = false,
    id = null,
    interviewSetIds = null,
    leaderboardEnabled = false,
    name = "",
    occurrenceAmount = null,
    occurrenceFrequency = null,
    selectedInterviewSets = [],
    user = null,
  }: InterviewGoalShape) {
    let extendedData = {
      continuous: continuous,
      createdAtFormatted: createdAtFormatted,
      description: description,
      hasErrors: hasErrors,
      id: id,
      leaderboardEnabled: leaderboardEnabled,
      occurrenceAmount: occurrenceAmount,
      occurrenceFrequency: occurrenceFrequency,
      name: name,
      user: user,
    };

    super(extendedData);
    this.hasErrors = hasErrors;
    this.type = GoalType.includedGoalTypes().find(
      (type: GoalTypeShape) => type.id == 1
    );
    this.interviewSetIds = this.formatInterviewSetIds(interviewSetIds);
    this.selectedInterviewSets = selectedInterviewSets;
    this.setSelectedInterviewSets();
    this.achievementTarget = achievementTargetFormatted
      ? achievementTargetFormatted
      : achievementTarget;
    this.achievementThreshold = achievementThreshold;
  }

  formatInterviewSetIds(interviewSetIds) {
    if (!interviewSetIds) return null;
    if (Array.isArray(interviewSetIds)) return interviewSetIds;
    return interviewSetIds.split(" ").map((id) => parseInt(id));
  }

  setSelectedInterviewSets() {
    if (!this.interviewSetIds || this.selectedInterviewSets.length != 0) return;
    var collectedInterviewSets = [];
    var collectedErrors = [];

    this.interviewSetIds.forEach((id) => {
      let interviewSetObject = InterviewSet.find_by_id(id);
      if (!interviewSetObject) {
        collectedErrors.push(true);
      } else {
        collectedErrors.push(false);
        collectedInterviewSets.push(interviewSetObject);
      }
    });
    this.hasErrors = collectedErrors.includes(true);
    this.selectedInterviewSets = collectedInterviewSets;
  }

  checkForEditorErrors(targetIds) {
    // ["5", "12", "27", "31"] -> from the scheduling tab.
    var collectedErrors = [];

    // Collects an error if any of the selected interviews cannot find a match in the
    // list of interviews from the scheduling tab.
    if (this.selectedInterviewSets) {
      this.selectedInterviewSets.forEach((set) => {
        if (
          !targetIds.find(
            (id) => set.find_target_interview_by_id(parseInt(id))?.id == id
          )
        ) {
          collectedErrors.push(true);
        } else {
          collectedErrors.push(false);
        }
      });
    } else {
      collectedErrors.push(true);
    }

    // Collects an error if original supply of interview ids cannot find a match in the original list of interview
    // ids saved to the monitor program.
    if (this.interviewSetIds) {
      this.interviewSetIds.forEach((id) => {
        let interviewObject = InterviewSet.find_by_id(id);
        if (!interviewObject) {
          collectedErrors.push(true);
        } else {
          collectedErrors.push(false);
        }
      });
    }

    this.hasErrors = collectedErrors.includes(true);
    return this.hasErrors;
  }

  formatForSave() {
    var interviewSetIds: number[] | null = this.selectedInterviewSets.map(
      (set) => set.id
    );

    var stringedInterviewSetIds = interviewSetIds.join(" ");
    var payload: SavePayloadShape = {
      achievement_condition: "greater_than_equal_to",
      achievement_target: this.achievementTarget,
      achievement_threshold: this.achievementThreshold,
      continuous: this.continuous,
      description: this.description,
      leaderboard_enabled: this.leaderboardEnabled,
      education_link_ids: null,
      id: this.id,
      interview_set_ids: stringedInterviewSetIds,
      measurement_id: null,
      name: this.name,
      occurrence_amount:
        this.occurrenceAmount == null ? 0 : this.occurrenceAmount,
      occurrence_frequency: this.occurrenceFrequency.name,
      type: this.type.name,
      user_id: this.user.id,
    };
    return payload;
  }
  formatForLocalSave() {
    var interviewSetIds: number[] | null = this.selectedInterviewSets.map(
      (interview) => interview.id
    );

    var stringedInterviewSetIds = interviewSetIds.join(" ");
    var payload: any = {
      achievementCondition: "greater_than_equal_to",
      achievementTarget: this.achievementTarget,
      achievementThreshold: this.achievementThreshold,
      continuous: this.continuous,
      createdAtFormatted: this.createdAtFormatted,
      description: this.description,
      leaderboardEnabled: this.leaderboardEnabled,
      educationLinkIds: null,
      id: this.id,
      interviewSetIds: stringedInterviewSetIds,
      measurementId: null,
      name: this.name,
      occurrenceAmount:
        this.occurrenceAmount == null ? 0 : this.occurrenceAmount,
      occurrenceFrequency: this.occurrenceFrequency.name,
      type: this.type.name,
      user: this.user,
    };
    return payload;
  }

  static complete = (goal: InterviewGoalShape) => {
    let results = [
      goal.achievementTarget,
      goal.achievementThreshold,
      goal.occurrenceFrequency,
      goal.continuous ? true : goal.occurrenceAmount,
    ];

    if (
      results.includes(null) ||
      goal.name == "" ||
      goal.selectedInterviewSets.length == 0
    ) {
      return false;
    } else {
      return true;
    }
  };
}
